// ./components/SideBar/SideBar.jsx
import React, { useState, useContext, useRef, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { FaQuestionCircle } from 'react-icons/fa';
import BarChartIcon from '@mui/icons-material/BarChart';
import AgricultureIcon from '@mui/icons-material/Agriculture';
import LayersIcon from '@mui/icons-material/Layers';
import SettingsIcon from '@mui/icons-material/Settings';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import GrassIcon from '@mui/icons-material/Grass';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import HistoryIcon from '@mui/icons-material/History';
import { useAuth } from '../../context/AuthContext';
import { Box, List, ListItem, ListItemText, CircularProgress } from '@mui/material';

import {
    SidebarWrapper,
    SidebarContainer,
    MenuItemsContainer,
    MenuItemContainer,
    MenuIconContainer,
    TooltipContainer,
    HelpButton,
    SubMenuContainer,
    FullscreenButton
} from './SideBarStyle';

import Tutorial from '../Tutorial/Tutorial';
import { SidebarContext } from '../../context/SidebarContext';
import HistorialContent from '../HistorialModal/HistorialModal';
import { API_BASE_PYTHON_SERVICE } from '../../utils/config';
import axios from 'axios';
import { PermissionContext } from '../../context/PermissionContext';

const GENERATED_PDF_REPORTS_STORAGE_KEY = 'geomotica_generated_pdf_reports';

const readGeneratedPdfReports = () => {
    try {
        const parsed = JSON.parse(localStorage.getItem(GENERATED_PDF_REPORTS_STORAGE_KEY) || '{}');
        return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
    } catch {
        return {};
    }
};

const mergePdfReports = (serverReports = [], analysisId) => {
    const localReportsMap = readGeneratedPdfReports();
    const localReports = analysisId && Array.isArray(localReportsMap[String(analysisId)])
        ? localReportsMap[String(analysisId)]
        : [];

    const unique = new Map();
    [...serverReports, ...localReports].forEach((report) => {
        if (!report) return;
        const key = report.gcs_path || report.download_url || report.file_name || report.signed_url;
        if (!key) return;
        unique.set(key, {
            ...report,
            analisis_id: report.analisis_id || analysisId,
            tipo_archivo: report.tipo_archivo || 'pdf'
        });
    });

    return Array.from(unique.values())
        .sort((a, b) => new Date(b.updated || 0) - new Date(a.updated || 0));
};

const Sidebar = ({ onSelectedFilesChange, selectedFilters }) => {
    const location = useLocation();
    const { logout } = useAuth();
    const { setSelectedSidebarOption } = useContext(SidebarContext);

    const { allow, loading, permissions } = useContext(PermissionContext);

    // ✅ Seguridad NO depende de permisos (ni del loading de permisos)
    const isSecurityRoute = useMemo(() => {
        const p = location.pathname || '';
        return p.includes('/security-module') || p.includes('/security-login');
    }, [location.pathname]);

    // ✅ Si ya hay permisos en memoria/caché, no “bloqueamos” el render aunque loading esté true por refresh
    const canRenderNonSecurityMenu = useMemo(() => {
        return !loading || (Array.isArray(permissions) && permissions.length > 0);
    }, [loading, permissions]);

    const [activeItem, setActiveItem] = useState(location.pathname);
    const [tooltip, setTooltip] = useState({ visible: false, content: '', position: { top: 0, left: 0 } });
    const [openSubMenus, setOpenSubMenus] = useState([]);
    const [isTutorialActive, setIsTutorialActive] = useState(false);
    const menuItemRefs = useRef({});
    const sidebarRef = useRef(null);
    const navigate = useNavigate();

    const [userData, setUserData] = useState(null);
    const [isFullscreen, setIsFullscreen] = useState(false);

    const [analyses, setAnalyses] = useState(null);
    const [loadingAnalyses, setLoadingAnalyses] = useState(true);
    const [selectedFiles, setSelectedFiles] = useState([]);
    const [activeSigOption, setActiveSigOption] = useState(null);

    // ============================
    // Persistencia de archivos
    // ============================
    useEffect(() => {
        const stored = localStorage.getItem('selectedFiles');
        if (stored) {
            setSelectedFiles(JSON.parse(stored));
        }
    }, []);
    useEffect(() => {
        localStorage.setItem('selectedFiles', JSON.stringify(selectedFiles));
        if (typeof onSelectedFilesChange === 'function') {
            onSelectedFilesChange(selectedFiles);
        }
    }, [selectedFiles, onSelectedFilesChange]);

    // ============================
    // Pantalla completa
    // ============================
    const toggleFullscreen = useCallback(() => {
        if (!document.fullscreenElement) {
            document.documentElement.requestFullscreen().catch((err) => {
                console.error(`Error al intentar activar pantalla completa: ${err.message} (${err.name})`);
            });
        } else if (document.exitFullscreen) {
            document.exitFullscreen();
        }
    }, []);
    useEffect(() => {
        const handleFullscreenChange = () => setIsFullscreen(!!document.fullscreenElement);
        document.addEventListener('fullscreenchange', handleFullscreenChange);
        return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
    }, []);

    // ============================
    // userData
    // ============================
    useEffect(() => {
        const storedUserData = localStorage.getItem('userData');
        if (storedUserData) {
            try {
                const parsedData = JSON.parse(storedUserData);
                setUserData(parsedData);
            } catch {
                setUserData(null);
            }
        }
    }, []);

    // ============================
    // Cargar análisis (Historial)
    // ============================
    const fetchAnalyses = useCallback(async () => {
        if (userData && userData.userId) {
            try {
                const params = {};
                if (selectedFilters?.zafra) params.zafra = selectedFilters.zafra;
                if (selectedFilters?.tercio) params.tercio = selectedFilters.tercio;
                if (selectedFilters?.finca) params.finca = selectedFilters.finca;
                if (selectedFilters?.operador) params.operador = selectedFilters.operador;

                const response = await axios.get(
                    `${API_BASE_PYTHON_SERVICE}mapping/analisis/${userData.userId}`,
                    { params, timeout: 15000 }
                );
                const data = response.data;

                if (data.status === 'success' && data.data) {
                    const sections = {
                        'Aplicaciones aéreas': [],
                        'Cosecha mecánica': [],
                        'Conteo de palmas': [],
                        'Fertilización': [],
                    };
                    data.data.forEach(analisisItem => {
                        const { archivos } = analisisItem;
                        const analysisId = analisisItem.analisis_id;
                        const reportesPdf = mergePdfReports(
                            Array.isArray(archivos.reportes_pdf) ? archivos.reportes_pdf : [],
                            analysisId
                        );
                        const attachReportes = (files = []) => files.map(file => ({
                            ...file,
                            reportes_pdf: reportesPdf
                        }));

                        let attachedToASection = false;

                        if (archivos.aplicaciones_aereas?.length) {
                            sections['Aplicaciones aéreas'].push(...attachReportes(archivos.aplicaciones_aereas));
                            attachedToASection = true;
                        }
                        if (archivos.cosecha_mecanica?.length) {
                            sections['Cosecha mecánica'].push(...attachReportes(archivos.cosecha_mecanica));
                            attachedToASection = true;
                        }
                        if (archivos.conteo_palma?.length) {
                            sections['Conteo de palmas'].push(...attachReportes(archivos.conteo_palma));
                            attachedToASection = true;
                        }
                        if (archivos.fertilizacion?.length) {
                            sections['Fertilización'].push(...attachReportes(archivos.fertilizacion));
                            attachedToASection = true;
                        }

                        // Si un análisis tiene PDF guardado pero todavía no tiene capa/listado visible,
                        // mostrar una fila sintética para que el PDF no quede oculto.
                        if (!attachedToASection && reportesPdf.length) {
                            const tipo = String(analisisItem.tipo_analisis || '').toUpperCase();
                            const sectionName =
                                tipo.includes('COSECHA') ? 'Cosecha mecánica' :
                                tipo.includes('PALMA') ? 'Conteo de palmas' :
                                tipo.includes('FERTIL') ? 'Fertilización' :
                                'Aplicaciones aéreas';

                            sections[sectionName].push({
                                key: `reporte_pdf_${analysisId}`,
                                file_name: `Reporte PDF - ${analysisId}`,
                                signed_url: null,
                                updated: reportesPdf[0]?.updated || analisisItem.fecha_creacion || null,
                                reportes_pdf: reportesPdf,
                                isPdfOnly: true
                            });
                        }
                    });
                    setAnalyses(sections);
                } else {
                    setAnalyses({ 'Aplicaciones aéreas': [], 'Cosecha mecánica': [], 'Conteo de palmas': [], 'Fertilización': [] });
                }
            } catch {
                setAnalyses({ 'Aplicaciones aéreas': [], 'Cosecha mecánica': [], 'Conteo de palmas': [], 'Fertilización': [] });
            } finally {
                setLoadingAnalyses(false);
            }
        } else {
            setLoadingAnalyses(false);
        }
    }, [userData, selectedFilters]);

    useEffect(() => {
        fetchAnalyses();
    }, [fetchAnalyses]);

    useEffect(() => {
        const refreshAfterPdfGeneration = () => {
            fetchAnalyses();
            window.setTimeout(fetchAnalyses, 1000);
        };
        window.addEventListener('geomotica:pdf-report-generated', refreshAfterPdfGeneration);
        return () => window.removeEventListener('geomotica:pdf-report-generated', refreshAfterPdfGeneration);
    }, [fetchAnalyses]);

    // ============================
    // Click de menú
    // ============================
    const handleMenuItemClick = useCallback(
        (item, event, level = 0) => {
            event.stopPropagation();

            if (item.name === 'Historial') {
                if (userData && userData.userId) {
                    const parentRect = menuItemRefs.current[item.name]?.getBoundingClientRect();
                    setOpenSubMenus((prev) => {
                        const newOpenSubMenus = prev.slice(0, level);
                        newOpenSubMenus[level] = { isHistorial: true, parentRect };
                        return newOpenSubMenus;
                    });
                }
                return;
            }

            if (item.subcategories) {
                const parentRect = menuItemRefs.current[item.name]?.getBoundingClientRect();
                setOpenSubMenus((prev) => {
                    const newOpenSubMenus = prev.slice(0, level);
                    newOpenSubMenus[level] = { items: item.subcategories, parentRect };
                    return newOpenSubMenus;
                });
            } else {
                if (item.toggleable) {
                    if (activeSigOption === item.name) {
                        setActiveSigOption(null);
                        setSelectedSidebarOption(null);
                    } else {
                        setActiveSigOption(item.name);
                        setSelectedSidebarOption(item.name);
                        if (item.path) {
                            setActiveItem(item.path);
                            navigate(item.path);
                        } else {
                            if (!location.pathname.startsWith('/mapeo')) {
                                setActiveItem('/mapeo');
                                navigate('/mapeo?open=SIGAgricola');
                            }
                        }
                    }
                } else {
                    setActiveSigOption(null);
                    if (item.name === 'Cerrar sesión') {
                        logout();
                        navigate('/login');
                        return;
                    }
                    if (item.path) {
                        setSelectedSidebarOption(item.name);
                        setActiveItem(item.path);
                        navigate(item.path);
                    }
                }
                setOpenSubMenus([]);
            }
        },
        [logout, navigate, setSelectedSidebarOption, userData, activeSigOption, location.pathname]
    );

    // ============================
    // Definición base (jerárquica)
    // ============================
    const baseMenuItems = useMemo(() => [
        { name: 'Dashboard', icon: <BarChartIcon />, path: '/dashboard', level: 0, moduleKey: 'dashboard' },
        { name: 'Mapeo de maquinaria', icon: <AgricultureIcon />, path: '/mapeo?tipo=mapeo_maquinaria', level: 0, moduleKey: 'mapeo', submoduleKey: 'mapeo_maquinaria' },

        // 🔓 Siempre visible
        { name: 'Procesamiento de fotos', icon: <GrassIcon />, path: '/mapeo?tipo=procesamiento_de_fotos', level: 0, moduleKey: 'mapeo', submoduleKey: 'procesamiento_de_fotos', alwaysVisible: true },

        {
            name: 'SIG Agrícola',
            icon: <LayersIcon />,
            level: 0,
            moduleKey: 'sig_agricola',
            subcategories: [
                {
                    name: 'Cosecha',
                    level: 1,
                    moduleKey: 'sig_agricola',
                    subcategories: [
                        { name: 'Planificación de cosecha', path: null, level: 2, toggleable: true, moduleKey: 'sig_agricola', submoduleKey: 'cosecha.planificacion' },
                        { name: 'Avance de cosecha', path: '/cosecha/avance', level: 2, toggleable: true, moduleKey: 'sig_agricola', submoduleKey: 'cosecha.avance' },
                        { name: 'Tipo de cosecha (mecánica / manual)', path: '/cosecha/tipo', level: 2, toggleable: true, moduleKey: 'sig_agricola', submoduleKey: 'cosecha.tipo' },
                        { name: 'Traciegos', path: '/cosecha/traciegos', level: 2, toggleable: true, moduleKey: 'sig_agricola', submoduleKey: 'cosecha.traciegos' },
                        { name: 'Quemas criminales', path: '/cosecha/quemas', level: 2, toggleable: true, moduleKey: 'sig_agricola', submoduleKey: 'cosecha.quemas' },
                        { name: 'Rutas cañeras', path: '/cosecha/rutas', level: 2, toggleable: true, moduleKey: 'sig_agricola', submoduleKey: 'cosecha.rutas' },
                    ]
                },
                {
                    name: 'Herbicidas',
                    level: 1,
                    moduleKey: 'sig_agricola',
                    subcategories: [
                        { name: 'Planificación de Herbicidas', path: null, level: 2, toggleable: true, moduleKey: 'sig_agricola', submoduleKey: 'herbicidas.planificacion' }
                    ]
                },
                {
                    name: 'Madurantes',
                    level: 1,
                    moduleKey: 'sig_agricola',
                    subcategories: [
                        { name: 'Planificacion de madurante', path: '/madurantes/planificacion', level: 2, toggleable: true, moduleKey: 'sig_agricola', submoduleKey: 'madurantes.planificacion' },
                        { name: 'Avance de madurante', path: '/madurantes/avance', level: 2, toggleable: true, moduleKey: 'sig_agricola', submoduleKey: 'madurantes.avance' },
                        { name: 'Dias programados para corte', path: null, level: 2, toggleable: true, moduleKey: 'sig_agricola', submoduleKey: 'madurantes.dias_programados' },
                        { name: 'Aplicacion con dron', path: '/madurantes/aplicacion-dron', level: 2, toggleable: true, moduleKey: 'sig_agricola', submoduleKey: 'madurantes.aplicacion_dron' },
                    ]
                },
                {
                    name: 'Variedades',
                    level: 1,
                    moduleKey: 'sig_agricola',
                    subcategories: [
                        { name: 'Tipos de variedades', path: '/variedades/tipos', level: 2, toggleable: true, moduleKey: 'sig_agricola', submoduleKey: 'variedades.tipos' },
                        { name: 'Variedades de más a menos productividad', path: null, level: 2, toggleable: true, moduleKey: 'sig_agricola', submoduleKey: 'variedades.ranking' },
                        { name: 'Semilleros', path: '/variedades/semilleros', level: 2, toggleable: true, moduleKey: 'sig_agricola', submoduleKey: 'variedades.semilleros' },
                        { name: 'Ensayos de variedades', path: '/variedades/ensayos', level: 2, toggleable: true, moduleKey: 'sig_agricola', submoduleKey: 'variedades.ensayos' },
                    ]
                },
                {
                    name: 'Plagas',
                    level: 1,
                    moduleKey: 'sig_agricola',
                    subcategories: [
                        { name: 'Barrenador', path: '/plagas/barrenador', level: 2, toggleable: true, moduleKey: 'sig_agricola', submoduleKey: 'plagas.barrenador' },
                        { name: 'Chinche salivosa', path: '/plagas/chinche', level: 2, toggleable: true, moduleKey: 'sig_agricola', submoduleKey: 'plagas.chinche' },
                        { name: 'Rata', path: null, level: 2, toggleable: true, moduleKey: 'sig_agricola', submoduleKey: 'plagas.rata' },
                        { name: 'Plagas del suelo', path: '/plagas/suelo', level: 2, toggleable: true, moduleKey: 'sig_agricola', submoduleKey: 'plagas.suelo' },
                        { name: 'Programación de muestreo semanal', path: '/plagas/programacion', level: 2, toggleable: true, moduleKey: 'sig_agricola', submoduleKey: 'plagas.programacion' },
                    ]
                },
                {
                    name: 'Riego',
                    level: 1,
                    moduleKey: 'sig_agricola',
                    subcategories: [
                        { name: 'Áreas con y sin riego', path: '/riego/areas', level: 2, toggleable: true, moduleKey: 'sig_agricola', submoduleKey: 'riego.areas' },
                        { name: 'Tipos de riego', path: '/riego/tipos', level: 2, toggleable: true, moduleKey: 'sig_agricola', submoduleKey: 'riego.tipos' },
                        { name: 'Balance hídrico', path: '/riego/balance', level: 2, toggleable: true, moduleKey: 'sig_agricola', submoduleKey: 'riego.balance' },
                        { name: 'Evapotranspiración', path: '/riego/evapotranspiracion', level: 2, toggleable: true, moduleKey: 'sig_agricola', submoduleKey: 'riego.evapotranspiracion' },
                        { name: 'Calidad del riego', path: '/riego/calidad', level: 2, toggleable: true, moduleKey: 'sig_agricola', submoduleKey: 'riego.calidad' },
                        { name: 'Productividad con y sin riego', path: null, level: 2, toggleable: true, moduleKey: 'sig_agricola', submoduleKey: 'riego.productividad' },
                    ]
                }
            ]
        },

        { name: 'Historial', icon: <HistoryIcon />, path: null, level: 0, moduleKey: 'historial' },
        { name: 'Configuración', icon: <SettingsIcon />, path: '/configuracion', level: 0, moduleKey: 'configuracion' },
        { name: 'Cerrar sesión', icon: <ExitToAppIcon />, path: '/logout', level: 0 }
    ], []);

    // ============================
    // Filtro por permisos
    // ============================
    const filterByPermissions = useCallback((items) => {
        const out = [];
        for (const item of items) {
            if (item.name === 'Cerrar sesión') {
                out.push(item);
                continue;
            }

            if (item.alwaysVisible === true || item.name === 'Procesamiento de fotos' || item.submoduleKey === 'procesamiento_de_fotos') {
                out.push(item);
                continue;
            }

            if (!item.subcategories) {
                const ok = allow(item.moduleKey || '', item.submoduleKey || null);
                if (ok) out.push(item);
                continue;
            }

            const moduleAllowed = allow(item.moduleKey || '', null);
            const allowedChildren = filterByPermissions(item.subcategories);

            if (moduleAllowed && allowedChildren.length > 0) {
                out.push({ ...item, subcategories: allowedChildren });
            }
        }
        return out;
    }, [allow]);

    const menuItems = useMemo(() => {
        if (isSecurityRoute) return [];
        if (!canRenderNonSecurityMenu) return [];
        return filterByPermissions(baseMenuItems);
    }, [isSecurityRoute, canRenderNonSecurityMenu, filterByPermissions, baseMenuItems]);

    // ============================
    // Menú de seguridad (sin filtro)
    // ============================
    const securityItems = useMemo(() => [
        { name: 'Control de Permisos por Usuario', icon: <SettingsIcon />, path: '/security-module/permisos', level: 0 },
        { name: 'Usuario', icon: <SettingsIcon />, path: '/security-module/agregar-usuario', level: 0 },
        { name: 'Empresa', icon: <SettingsIcon />, path: '/security-module/agregar-empresa', level: 0 },
        { name: 'Cerrar sesión', icon: <ExitToAppIcon />, path: '/security-login', level: 0 }
    ], []);

    const activeItems = useMemo(() => {
        return isSecurityRoute ? securityItems : menuItems;
    }, [isSecurityRoute, securityItems, menuItems]);

    // ============================
    // Tooltip
    // ============================
    const showTooltip = useCallback((content, event) => {
        const rect = event.currentTarget.getBoundingClientRect();
        const position = {
            top: rect.top + window.scrollY,
            left: rect.right + 10
        };
        setTooltip({ visible: true, content, position });
    }, []);
    const hideTooltip = useCallback(() => {
        setTooltip({ visible: false, content: '', position: { top: 0, left: 0 } });
    }, []);

    // ============================
    // Cerrar submenús al hacer click fuera
    // ============================
    const handleClickOutside = useCallback((event) => {
        if (sidebarRef.current && !sidebarRef.current.contains(event.target)) {
            setOpenSubMenus([]);
        }
    }, []);
    useEffect(() => {
        document.addEventListener('click', handleClickOutside);
        return () => document.removeEventListener('click', handleClickOutside);
    }, [handleClickOutside]);

    // ============================
    // Selección de archivos (Historial)
    // ============================
    const handleCheckboxChange = useCallback((file) => {
        setSelectedFiles(prevSelected => {
            const index = prevSelected.findIndex(selected => selected.file_name === file.file_name);
            if (index > -1) {
                const updated = [...prevSelected];
                updated.splice(index, 1);
                return updated;
            }
            return [...prevSelected, file];
        });
    }, []);

    // ============================
    // Abrir submenús por query param
    // ============================
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const openParam = params.get('open');

        // ✅ En rutas normales, esperamos a tener menú renderizable (no necesariamente loading=false)
        if (!isSecurityRoute && !canRenderNonSecurityMenu) return;

        if (openParam === 'SIGAgricola') {
            const item = activeItems.find(i => i.name === 'SIG Agrícola');
            if (item) {
                const fakeEvent = { stopPropagation: () => {} };
                handleMenuItemClick(item, fakeEvent, 0);
            }
        }
        if (openParam === 'historial') {
            const item = activeItems.find(i => i.name === 'Historial');
            if (item) {
                const fakeEvent = { stopPropagation: () => {} };
                handleMenuItemClick(item, fakeEvent, 0);
            }
        }
    }, [location.search, activeItems, handleMenuItemClick, isSecurityRoute, canRenderNonSecurityMenu]);

    return (
        <>
            <SidebarWrapper ref={sidebarRef}>
                <SidebarContainer>
                    <MenuItemsContainer>
                        {/* Loader SOLO para rutas NO-seguridad */}
                        {!isSecurityRoute && loading && (
                            <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center', py: 2 }}>
                                <CircularProgress size={22} />
                            </Box>
                        )}

                        {/* Seguridad siempre renderiza items aunque permisos estén cargando */}
                        {(isSecurityRoute || canRenderNonSecurityMenu) && activeItems.map((item, index) => (
                            <Box key={index} style={{ width: '100%' }}>
                                <MenuItemContainer
                                    ref={(el) => (menuItemRefs.current[item.name] = el)}
                                    active={activeItem === item.path}
                                    onClick={(e) => handleMenuItemClick(item, e, 0)}
                                    onMouseEnter={(e) => showTooltip(item.name, e)}
                                    onMouseLeave={hideTooltip}
                                >
                                    <MenuIconContainer>{item.icon}</MenuIconContainer>
                                </MenuItemContainer>
                            </Box>
                        ))}
                    </MenuItemsContainer>

                    <Box style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', paddingBottom: '20px' }}>
                        <HelpButton onClick={() => setIsTutorialActive(true)}>
                            <FaQuestionCircle />
                        </HelpButton>
                        <FullscreenButton onClick={toggleFullscreen}>
                            {isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
                        </FullscreenButton>
                    </Box>
                </SidebarContainer>

                {openSubMenus.map((submenu, level) => (
                    <SubMenuContainer
                        key={level}
                        style={{ top: submenu.parentRect.top, left: submenu.parentRect.right }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        {submenu.isHistorial ? (
                            <HistorialContent
                                analyses={analyses || { 'Aplicaciones aéreas': [], 'Cosecha mecánica': [], 'Conteo de palmas': [], 'Fertilización': [] }}
                                loading={loadingAnalyses}
                                onClose={() => setOpenSubMenus(prev => prev.slice(0, level))}
                                selectedFiles={selectedFiles}
                                onCheckboxChange={handleCheckboxChange}
                            />
                        ) : (
                            <List>
                                {submenu.items.map((item, idx) => (
                                    <ListItem
                                        button
                                        key={idx}
                                        selected={item.toggleable ? activeSigOption === item.name : false}
                                        ref={(el) => { if (el) menuItemRefs.current[item.name] = el; }}
                                        onClick={(e) => handleMenuItemClick(item, e, level + 1)}
                                        onMouseEnter={(e) => showTooltip(item.name, e)}
                                        onMouseLeave={hideTooltip}
                                    >
                                        <ListItemText primary={item.name} />
                                        {item.subcategories && <ExpandMoreIcon />}
                                    </ListItem>
                                ))}
                            </List>
                        )}
                    </SubMenuContainer>
                ))}
            </SidebarWrapper>

            {tooltip.visible && (
                <TooltipContainer top={tooltip.position.top} left={tooltip.position.left}>
                    {tooltip.content}
                </TooltipContainer>
            )}

            <Tutorial isActive={isTutorialActive} onClose={() => setIsTutorialActive(false)} />
        </>
    );
};

export default Sidebar;
